import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@punchup/utils/cn";

const buttonVariants = cva(
  `
  inline-flex
  items-center
  justify-center
  rounded-lg
  text-sm
  font-medium
  ring-offset-white
  transition-colors
  focus-visible:outline-none
  focus-visible:ring-2
  focus-visible:ring-primary
  focus-visible:ring-offset-2
  disabled:pointer-events-none
  disabled:opacity-60
  disabled:hover:shadow-none
  dark:ring-offset-neutral-950
  dark:focus-visible:ring-neutral-300
  shadow-sm
  hover:shadow-md
  cursor-pointer
  touch-manipulation
  `,
  {
    variants: {
      variant: {
        default: `
            bg-primary
            text-white
            font-semibold
            hover:brightness-125
            disabled:bg-opacity-50
            disabled:brightness-100
            disabled:opacity-100
            border-[1.5px]
            border-transparent
        `,
        outline: `
            font-semibold
            hover:bg-gray-100
            border-[1.5px]
            border-primary
            text-primary
            hover:bg-slate-100
        `,
        plain: `
            border
            border-neutral-200
            bg-white
            hover:bg-neutral-100
            hover:text-neutral-900
            dark:border-neutral-800
            dark:bg-neutral-950
            dark:hover:bg-neutral-800
            dark:hover:text-neutral-50
            focus-visible:ring-transparent
        `,
        ghost: `
            font-semibold
            border-[1.5px]
            border-transparent
            hover:bg-gray-100
            hover:border-[1.5px]
            hover:border-primary
            
            text-primary
            hover:bg-slate-100
         `,
        destructive: `
            bg-destructive
            text-white
            font-semibold
            hover:brightness-125
            disabled:bg-opacity-40
            disabled:brightness-100
            disabled:opacity-100
        `,
        destructiveOutline: `
            font-semibold
            border-[1.5px]
            border-destructive
            hover:bg-gray-100
            hover:border-[1.5px]
            hover:border-destructive
            text-destructive
            hover:bg-slate-100
            `,
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface PunchupButtonProps
  extends
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const PunchupButton = React.forwardRef<HTMLButtonElement, PunchupButtonProps>(
  ({ className, variant, size, asChild = false, onClick, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";

    const handleClick = React.useCallback(
      (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        if (onClick && !props.disabled) {
          onClick(e);
        }
      },
      [onClick, props.disabled]
    );

    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        onClick={handleClick}
        {...props}
      />
    );
  }
);
PunchupButton.displayName = "PunchupButton";

export { PunchupButton, buttonVariants };
